<template>
    <div class="space-y-6">
        <!-- API 管理卡片 -->
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg">
                    <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    API 管理
                </h2>
                <p class="text-sm text-base-content opacity-70">管理 API 访问令牌、认证配置和接口文档</p>
                
                <!-- 认证管理部分 -->
                <div class="mt-6">
                    <h3 class="font-medium text-base mb-3 flex items-center gap-2">
                        <svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                        </svg>
                        认证管理
                    </h3>
                    
                    <div class="form-control">
                        <label class="label">
                            <span class="label-text font-medium">Access Token</span>
                        </label>
                        <div class="space-y-2">
                            <input 
                                type="text" 
                                v-model="formData.access_token" 
                                class="input input-bordered w-full" 
                                placeholder="请输入或生成访问令牌"
                                @change="$emit('update-sys', 'access_token')" 
                            />
                            <div class="flex gap-2 flex-col sm:flex-row mt-2">
                                <button class="btn btn-primary flex-1 sm:flex-none btn-sm" @click="generateToken">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"></path>
                                    </svg>
                                    生成Token
                                </button>
                                <button class="btn btn-ghost flex-1 sm:flex-none btn-sm" @click="copyToken">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                    </svg>
                                    复制
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <div class="alert mt-4">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <div class="text-sm">
                            <div class="font-medium mb-1">使用说明</div>
                            <ul class="text-xs space-y-1">
                                <li>• Token 用于 API 认证，请在请求头中携带</li>
                                <li>• 生成新Token会立即使旧Token失效</li>
                                <li>• 请妥善保管Token，不要泄露给他人</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- 开放 API 部分 -->
                <div>
                    <h3 class="font-medium text-base mb-3 flex items-center gap-2">
                        <svg class="w-4 h-4 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        开放 API 接口
                    </h3>

                    <div class="overflow-x-auto">
                        <table class="table table-zebra">
                            <thead>
                                <tr>
                                    <th>API 名称</th>
                                    <th>功能描述</th>
                                    <th>请求方式</th>
                                    <th>认证方式</th>
                                    <th>状态</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(api, index) in apiList">
                                    <tr>
                                        <td class="font-medium">{{ api.name }}</td>
                                        <td>{{ api.description }}</td>
                                        <td>
                                            <span class="badge badge-outline badge-sm">
                                                {{ api.method }}
                                            </span>
                                        </td>
                                        <td class="text-sm">{{ api.auth_type }}</td>
                                        <td>
                                            <div class="badge badge-success text-white text-xs" v-if="api.status === 'normal'">
                                                正常
                                            </div>
                                            <div class="badge badge-error text-white text-xs" v-else>
                                                异常
                                            </div>
                                        </td>
                                        <td>
                                            <button class="btn btn-ghost btn-xs" @click="toggleParams(api)">
                                                {{ api.show ? '收起' : '文档' }}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="api.show">
                                        <td colspan="6" class="p-0">
                                            <div class="p-4 bg-base-200">
                                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <div class="mb-3">
                                                            <h4 class="font-medium text-sm mb-1">接口地址</h4>
                                                            <div class="bg-base-100 rounded p-2">
                                                                <code class="text-xs">{{ api.endpoint }}</code>
                                                            </div>
                                                        </div>
                                                        
                                                        <div class="mb-3">
                                                            <h4 class="font-medium text-sm mb-1">请求头</h4>
                                                            <div class="bg-base-100 rounded p-2">
                                                                <div v-for="(value, key) in api.headers" class="text-xs">
                                                                    <span class="text-primary">{{ key }}:</span> {{ value }}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div>
                                                        <div class="mb-3">
                                                            <h4 class="font-medium text-sm mb-1">请求参数</h4>
                                                            <div class="bg-base-100 rounded p-2">
                                                                <div v-if="api.params && api.params.length > 0" class="space-y-1">
                                                                    <div v-for="param in api.params" class="text-xs">
                                                                        <div class="flex items-center gap-1">
                                                                            <span class="font-medium text-primary">{{ param.name }}</span>
                                                                            <span class="badge badge-neutral badge-xs">{{ param.type }}</span>
                                                                            <span v-if="param.required" class="badge badge-error badge-xs text-white">必填</span>
                                                                        </div>
                                                                        <div class="text-xs opacity-70">{{ param.description }}</div>
                                                                    </div>
                                                                </div>
                                                                <div v-else class="text-xs opacity-70">无参数</div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div>
                                                            <h4 class="font-medium text-sm mb-1">返回示例</h4>
                                                            <div class="bg-base-100 rounded p-2">
                                                                <pre class="text-xs whitespace-pre-wrap">{{ api.response_example }}</pre>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- AI大模型接入卡片 -->
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg">
                    <svg class="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                    </svg>
                    AI大模型接入
                </h2>
                <p class="text-sm text-base-content opacity-70">配置 AI 服务以增强系统功能</p>
                
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    <div class="space-y-4">
                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">对话 URL</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.llm_base_url" 
                                class="input input-bordered" 
                                placeholder="请输入完整的对话接口, 结尾包含chat/completions"
                                @change="$emit('update-sys', 'llm_base_url')" 
                            />
                            <label class="label">
                                <span class="label-text-alt">例如：https://api.deepseek.com/v1/chat/completions</span>
                            </label>
                        </div>
                        
                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">API Key</span>
                            </label>
                            <input 
                                type="password" 
                                v-model="formData.llm_api_key" 
                                class="input input-bordered" 
                                placeholder="例如：sk-1234567890"
                                @change="$emit('update-sys', 'llm_api_key')" 
                            />
                        </div>
                        
                        <div class="form-control">
                            <label class="label">
                                <span class="label-text font-medium">模型名称</span>
                            </label>
                            <input 
                                type="text" 
                                v-model="formData.llm_model_name" 
                                class="input input-bordered" 
                                placeholder="例如：gpt-3.5-turbo"
                                @change="$emit('update-sys', 'llm_model_name')" 
                            />
                        </div>
                    </div>
                    
                    <div class="alert">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"></path>
                        </svg>
                        <div class="text-sm">
                            <div class="font-medium mb-1">配置说明</div>
                            <ul class="text-xs space-y-1">
                                <li>• 请确保 API 接口地址完整且可访问</li>
                                <li>• URL的路径是完整会话，不要省略结尾的chat/completions</li>
                                <li>• API Key 将加密保存，请妥善保管</li>
                                <li>• 模型名称需要与服务商提供的名称一致</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div class="mt-6">
                    <h3 class="font-medium mb-3 flex items-center gap-2">
                        <svg class="w-4 h-4 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                        </svg>
                        功能列表
                    </h3>
                    
                    <div class="overflow-x-auto">
                        <table class="table table-zebra">
                            <thead>
                                <tr>
                                    <th>功能</th>
                                    <th>描述</th>
                                    <th>功能位置</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="font-medium">AI补充音乐风格</td>
                                    <td class="text-sm">AI补充音乐风格，根据歌曲内容，给出音乐风格</td>
                                    <td class="text-xs">
                                        <div>1. 首页-完整度详情-流派规则刮削</div>
                                        <div>2. 操作台-元数据详情-AI识别风格</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-medium">AI翻译歌词</td>
                                    <td class="text-sm">AI翻译歌词，根据歌曲内容，给出翻译后的歌词</td>
                                    <td class="text-xs">1. 操作台-元数据详情-AI翻译歌词</td>
                                </tr>
                                <tr>
                                    <td class="font-medium">AI生成的正则表达式</td>
                                    <td class="text-sm">AI生成的正则表达式，根据歌曲内容，给出正则表达式</td>
                                    <td class="text-xs">1. 操作台-元数据详情-拆分元数据/替换文本-预览-AI生成的正则表达式</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OpenApi',
    props: {
        formData: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update-sys'],
    data() {
        return {
            apiList: []
        }
    },
    created() {
        this.fetchOpenApi()
    },
    methods: {
        fetchOpenApi() {
            this.$api.Task.openApi().then((res) => {
                if (res.result) {
                    this.apiList = res.data
                }
            })
        },
        generateToken() {
            // 生成随机token
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
            let token = ''
            for (let i = 0; i < 32; i++) {
                token += chars.charAt(Math.floor(Math.random() * chars.length))
            }
            this.formData.access_token = token
            this.$emit('update-sys', 'access_token')

            this.$myMsg.notify({
                title: '新Token已生成',
                type: 'success',
                time: 2000,
            })
        },

        copyToken() {
            if (this.formData.access_token) {
                navigator.clipboard.writeText(this.formData.access_token).then(() => {
                    this.$myMsg.notify({
                        title: 'Token已复制到剪贴板',
                        type: 'success',
                        time: 1000,
                    })
                })
            } else {
                this.$myMsg.notify({
                    title: '请先生成Token',
                    type: 'warning',
                    time: 1000,
                })
            }
        },

        toggleParams(api) {
            api.show = !api.show;
        },
    }
}
</script>